import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, CheckCircle2, AlertTriangle, Download, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { downloadBlob } from "@/lib/csv";

function Stat({ label, value, accent, testId }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">{label}</p>
      <p className={`font-serif text-3xl mt-1 ${accent ? "text-[#7A2021]" : "text-[#1A1714]"}`} data-testid={testId}>
        {(value ?? 0).toLocaleString()}
      </p>
    </div>
  );
}

export default function BulkDeleteDialog({ open, onOpenChange, onDeleted }) {
  // `job` mirrors the server job state: {job_id, total, processed, deleted,
  // not_found, skipped, progress, status, done, rows_preview}
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const reset = () => {
    stopPoll();
    setJob(null);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => { reset(); onOpenChange(false); };

  // Never leave an interval running if the dialog unmounts mid-job.
  useEffect(() => () => stopPoll(), []);

  const startPoll = (jobId) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/books/bulk-delete/" + jobId);
        setJob(data);
        if (data.done) {
          stopPoll();
          setBusy(false);
          if (data.status === "error") {
            toast.error("Bulk delete stopped: " + (data.error || "unknown error"));
          } else {
            if (data.deleted > 0) toast.success("Deleted " + data.deleted.toLocaleString() + (data.deleted === 1 ? " listing" : " listings"));
            if (data.not_found > 0) toast.warning(data.not_found.toLocaleString() + " identifier" + (data.not_found === 1 ? "" : "s") + " not found");
          }
          onDeleted?.();
        }
      } catch (err) {
        stopPoll();
        setBusy(false);
        toast.error("Lost contact with the delete job - reopen to check the report");
      }
    }, 1000);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!window.confirm(
      "This will PERMANENTLY delete matching listings.\n\nUpload \"" + f.name + "\"?\n\nThis action cannot be undone."
    )) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      // Returns immediately with a job_id; the work runs server-side in
      // batches so a large file cannot time out at the edge.
      const { data } = await api.post("/admin/books/bulk-delete", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJob({
        job_id: data.job_id, total: data.total, processed: 0, deleted: 0,
        not_found: 0, skipped: 0, progress: 0, status: "queued",
        done: false, rows_preview: [],
      });
      startPoll(data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed to start");
      reset();
    }
  };

  const downloadReport = async () => {
    if (!job?.job_id) return;
    try {
      // Full per-row report comes from the server, not the polling payload.
      const resp = await api.get("/admin/books/bulk-delete/" + job.job_id + "/report", { responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadBlob(new Blob([resp.data], { type: "text/csv" }), "bulk-delete-report-" + stamp + ".csv");
    } catch (err) {
      toast.error("Could not download the report");
    }
  };

  const running = job && !job.done;
  const finished = job && job.done;
  const previewRows = job?.rows_preview || [];
  const reportedRows = (job?.deleted || 0) + (job?.not_found || 0) + (job?.skipped || 0);

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent
        className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-3xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0"
        data-testid="bulk-delete-dialog"
      >
        <DialogHeader className="p-6 border-b border-[#E5E1D8]">
          <DialogTitle className="font-serif text-3xl text-[#1A1714] flex items-center gap-2">
            <Trash2 size={22} className="text-[#7A2021]" /> Bulk delete listings
          </DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            Upload the End-Listing Loader (.xlsx or .csv). Columns: <strong>Action, ISBN/SKU/ASIN, EndCode</strong>.
            Each row's identifier is matched against ISBN or SKU and deleted. Rows with Action ≠ End are skipped.
          </DialogDescription>
        </DialogHeader>

        {!job && (
          <div className="p-10 flex-1 flex flex-col items-center justify-center">
            <label
              htmlFor="bulk-delete-input"
              className={`cursor-pointer border-2 border-dashed w-full max-w-xl p-16 text-center transition-colors ${busy ? "border-slate-300 opacity-60" : "border-[#7A2021]/40 hover:border-[#7A2021]"}`}
              data-testid="bulk-delete-drop"
            >
              <Upload className="mx-auto text-[#7A2021]/70" size={40} />
              <p className="mt-4 font-serif text-2xl text-[#1A1714]">Drop the End-Listing file, or click to choose</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">
                .xlsx or .csv · Columns: Action · ISBN/SKU/ASIN · EndCode
              </p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-editorial text-[#7A2021]">
                ⚠️ Deletions are permanent and cannot be undone
              </p>
              <input
                id="bulk-delete-input"
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={onFile}
                disabled={busy}
                className="hidden"
                data-testid="bulk-delete-file-input"
              />
            </label>
            {busy && <p className="mt-6 font-serif italic text-lg text-[#1A1714]/60">Processing…</p>}
          </div>
        )}

        {running && (
          <div className="p-10 flex-1 flex flex-col items-center justify-center gap-6" data-testid="bulk-delete-progress">
            <p className="font-serif text-2xl text-[#1A1714]">
              Deleting&hellip; {(job.processed || 0).toLocaleString()} of {(job.total || 0).toLocaleString()} rows
            </p>
            <div className="w-full max-w-xl h-2 bg-[#E5E1D8]">
              <div className="h-2 bg-[#7A2021] transition-all" style={{ width: (job.progress || 0) + "%" }} />
            </div>
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50">
              {job.progress || 0}% &middot; you can close this, the job keeps running on the server
            </p>
          </div>
        )}

        {finished && (
          <div className="p-6 flex-1 flex flex-col gap-6" data-testid="bulk-delete-result">
            {job.status === "error" && (
              <p className="font-mono text-xs text-[#7A2021] border border-[#7A2021]/30 bg-[#7A2021]/5 p-3" data-testid="bulk-delete-error">
                The job stopped early: {job.error || "unknown error"}. Listings already deleted are shown below and in the report.
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-y border-[#E5E1D8] py-6">
              <Stat label="Rows" value={job.total} testId="bulk-delete-stat-total" />
              <Stat label="Deleted" value={job.deleted} accent testId="bulk-delete-stat-deleted" />
              <Stat label="Not found" value={job.not_found} testId="bulk-delete-stat-notfound" />
              <Stat label="Skipped" value={job.skipped} testId="bulk-delete-stat-skipped" />
            </div>

            <div className="flex-1 overflow-y-auto border border-[#E5E1D8]">
              <table className="w-full text-xs" data-testid="bulk-delete-rows">
                <thead className="bg-[#F3EFE6] sticky top-0">
                  <tr className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
                    <th className="px-4 py-2 text-left w-14">Row</th>
                    <th className="px-4 py-2 text-left">Identifier</th>
                    <th className="px-4 py-2 text-left">Matched title</th>
                    <th className="px-4 py-2 text-left w-28">Status</th>
                    <th className="px-4 py-2 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r) => (
                    <tr key={r.row_number} className="border-t border-[#E5E1D8]">
                      <td className="px-4 py-2 font-mono">{r.row_number}</td>
                      <td className="px-4 py-2 font-mono">{r.identifier || "—"}</td>
                      <td className="px-4 py-2 font-serif italic text-[#1A1714]/70">{r.matched_title || "—"}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-editorial px-2 py-0.5 border rounded-md ${
                          r.status === "deleted" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                          r.status === "not_found" ? "border-red-200 bg-red-50 text-red-700" :
                          "border-amber-200 bg-amber-50 text-amber-700"
                        }`}>
                          {r.status === "deleted" ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />} {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-[#1A1714]/70">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {reportedRows > previewRows.length && (
              <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/50" data-testid="bulk-delete-truncated">
                Showing the first {previewRows.length.toLocaleString()} of {reportedRows.toLocaleString()} rows &middot; download the report for the full list
              </p>
            )}

            <div className="flex justify-between items-center gap-3 border-t border-[#E5E1D8] pt-5">
              <p className="font-serif text-lg text-[#1A1714]">
                Done. <em className="text-[#7A2021]">{(job.deleted || 0).toLocaleString()}</em> deleted · <em>{(job.not_found || 0).toLocaleString()}</em> not found · <em>{(job.skipped || 0).toLocaleString()}</em> skipped
              </p>
              <div className="flex gap-3 flex-wrap">
                <Button
                  onClick={downloadReport}
                  variant="outline"
                  data-testid="bulk-delete-download-report"
                  className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
                >
                  <Download size={14} className="mr-2" /> Download report
                </Button>
                <Button
                  onClick={reset}
                  variant="outline"
                  data-testid="bulk-delete-again"
                  className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
                >
                  Upload another
                </Button>
                <Button
                  onClick={close}
                  data-testid="bulk-delete-close"
                  className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
